/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { memo, useContext, useEffect, useState } from 'react';
import IEnergyManagementProps, {
  IAny,
  ICarbonAvoidanceData,
  IRequestContext
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import TotalEmissionsDetailsComponent from '../TotalEmissionDetailWidget/TotalEmissionDetailWidget';
import TotalEmissionDetailwidgetSeparateAPI from '../TotalEmissionDetailWidget/TotalEmissionDetailWidgetSeparateAPI';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import TotalEmissionsSavingsByMeterComponent from '../TotalEmissionsSavingsByMeterWidget/TotalEmissionsSavingsByMeterComponent';
import {
  formattedDate,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import { getCarbonVDEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { Icon } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import './TotalEmissionsDetailsComponentTheme.scss';
import { ApiContext } from '../../../../context/ApiContext';
import CarbonAvoidanceSummaryWidget from '../../PortfolioCarbonSiteViewDetailsComponent/PortfolioCarbonAvoidance/CarbonAvoidanceSummaryWidget';
import {
  EmaasSubscriptionType,
  SmartPowerSubscriptionType
} from '../../../shared/Enums';
import CarbonAvoidanceService from '../../PortfolioCarbonSiteViewDetailsComponent/PortfolioCarbonAvoidance/CarbonAvoidanceService';
import TotalEmissionsSavingsByMeterComponentSeparate from '../TotalEmissionsSavingsByMeterWidget/TotalEmissionsSavingsByMeterComponentSeparate';

const TotalEmissionsTabDetailsComponent = (props: IEnergyManagementProps) => {
  const { bearerToken, requestContext } = props;
  const isMounted = useMounted();
  const d = descriptors.DetailedView;
  const [t] = useTranslation();
  const emaasContext = useContext(ApiContext);
  const apiContext = useContext(ApiContext);
  const dataSource = requestContext.context.dataSource || '';

  const isPremiumSubscription =
    requestContext.context.smpSubscriptionType ===
    SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
      ? true
      : false;

  const isSitePremium = apiContext.toggleSeparateApi;

  const getAdjustedEndDate = (userEndDate: string | Date) => {
    // Parse the user's end date
    const userDate = new Date(userEndDate);
    const currentDate = new Date();

    // Check if the user's end date month and year are the same as the current month and year
    if (
      userDate.getMonth() === currentDate.getMonth() &&
      userDate.getFullYear() === currentDate.getFullYear()
    ) {
      // Subtract one month from the user's end date
      userDate.setMonth(userDate.getMonth() - 1);
    }

    return userDate.toISOString();
  };

  const getConnectorConfigCarbon = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    console.log('requestContext', requestContext);
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    console.log('getAdjustedEndDate', getAdjustedEndDate);
    if (toggleFeatureScopeOne) {
      return {
        // name: 'site-carbon-new',
        name: isSitePremium ? 'carbon-site-summary' : 'site-carbon-new',
        options: {
          ...params,
          scopeType: 'all',
          dataSource: isSitePremium ? dataSource : ''
        }
      };
    } else {
      return {
        name: isSitePremium ? 'carbon-site-summary' : 'carbon-site',
        options: {
          ...params,
          allDetails: 'true',
          utilityType: 'all',
          dataSource: isSitePremium ? dataSource : ''
        }
      };
      // return {
      //   // name: 'site-carbon-new',
      //   name: isSitePremium ? 'carbon-site-summary-v1' : 'site-carbon-new-v1',
      //   options: {
      //     ...params,
      //     scopeType: 'all'
      //   }
      // };
    }
  };

  const getConnectorConfigGraph = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'site-carbon-new-graph',
        options: {
          ...params,
          scopeType: 'all',
          dataSource
        }
      };
    } else {
      return {
        name: 'carbon-site-Graph',
        options: {
          ...params,
          allDetails: 'true',
          utilityType: 'all',
          dataSource
        }
      };
    }
  };

  const getConnectorConfigTable = (
    toggleFeatureScopeOne: boolean | undefined,
    requestContext: IRequestContext
  ) => {
    const params = {
      startDate: requestContext.context.startTime
        ? formattedDate(requestContext.context.startTime)
        : '',
      endDate: getCarbonVDEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        dataSource
      )
    };
    if (toggleFeatureScopeOne) {
      return {
        name: 'site-carbon-new-table',
        options: {
          ...params,
          groupType: 'category',
          dataSource
        }
      };
    } else {
      return {
        name: 'carbon-site-table',
        options: {
          ...params,
          allDetails: 'true',
          groupType: 'category',
          dataSource
        }
      };
    }
  };

  const [noDataCount, setnoDataCount] = useState<number>(0);
  const { toggleFeatureScopeOne } = emaasContext!;

  const connectorConfig = getConnectorConfigCarbon(
    toggleFeatureScopeOne,
    requestContext
  );

  const connectorConfigForGraph = getConnectorConfigGraph(
    toggleFeatureScopeOne,
    requestContext
  );

  const connectorConfigTable = getConnectorConfigTable(
    toggleFeatureScopeOne,
    requestContext
  );
  const params = {
    startDate: requestContext.context.startTime
      ? formattedDate(requestContext.context.startTime)
      : '',
    endDate: getCarbonVDEndDateBasedOnSub(
      requestContext.context.subscriptionType!,
      requestContext.context.endTime!,
      requestContext.context.startTime!
    )
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorForGraph: any;
  dataConnectorForGraph = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigForGraph,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];

  let dataConnectorTable: any;
  [dataConnectorTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigTable,
      requestContext,
      bearerToken
    },
    isSitePremium
  );

  useEffect(() => {
    if (isMounted) {
      dataConnector?.actions?.getAll();
      if (isSitePremium) {
        dataConnectorForGraph &&
          dataConnectorForGraph.actions &&
          dataConnectorForGraph.actions.getAll();
        dataConnectorTable &&
          dataConnectorTable.actions &&
          dataConnectorTable.actions.getAll();
        dataConnectorMissingData?.actions?.getAll();
      }
    }
  }, [requestContext, dataSource]);

  const connectorConfigMissigData = {
    name: 'get-missing-data-details-forPeriod',
    options: {
      ...params
    }
  };
  const [dataConnectorMissingData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigMissigData,
    requestContext,
    bearerToken
  });

  const { isFromSmartPower } = apiContext!;

  const {
    all: allMissingData,
    loading: loadingMissingData,
    error: errorMissingData,
    status: statusMissingData
  } = dataConnectorMissingData.data;
  const MissingDataCondition = allMissingData as IAny;
  const isMissingDataAvailable = MissingDataCondition ? true : false;

  useEffect(() => {
    if (
      (isMissingDataAvailable || errorMissingData || statusMissingData) &&
      !loadingMissingData
    ) {
      setnoDataCount(MissingDataCondition.totalAlerts);
    }
  }, [isMissingDataAvailable, loadingMissingData, statusMissingData]);

  const c02AvoidanceData:
    | ICarbonAvoidanceData
    | undefined = isPremiumSubscription
    ? CarbonAvoidanceService(bearerToken ?? 'bearer ', requestContext)
    : undefined;

  return (
    <div className="total-emissions-details">
      {toggleFeatureScopeOne && noDataCount > 0 ? (
        <div className="totalEmissionContainer">
          <img
            className="alarm-meter-icon"
            src={require('../../../../images/information.svg')}
          />
          <div>{t(d.SCOPEONEDATANOTCONSIDER)}</div>
        </div>
      ) : (
        ''
      )}

      {isSitePremium ? (
        <TotalEmissionDetailwidgetSeparateAPI
          isSite
          dataConnector={dataConnector}
          dataConnectorForGraph={dataConnectorForGraph}
          avoidanceData={c02AvoidanceData}
          requestContext={requestContext}
          dataSourceType={props.dataSourceType ? props.dataSourceType : ''}
          // dataSourceType={props.dataSourceType}
        />
      ) : (
        <TotalEmissionsDetailsComponent
          isSite
          dataConnector={dataConnector}
          avoidanceData={c02AvoidanceData}
          requestContext={requestContext}
        />
      )}
      {isSitePremium ? (
        <TotalEmissionsSavingsByMeterComponentSeparate
          toggleFeatureScopeOne={toggleFeatureScopeOne}
          dataConnectorTable={dataConnectorTable}
          dataConnector={dataConnector}
        />
      ) : (
        <TotalEmissionsSavingsByMeterComponent
          toggleFeatureScopeOne={toggleFeatureScopeOne}
          dataConnector={dataConnector}
        />
      )}
      {isPremiumSubscription && (
        <CarbonAvoidanceSummaryWidget
          bearerToken={bearerToken ?? 'bearer '}
          requestContext={requestContext}
        />
      )}
    </div>
  );
};
export default memo(TotalEmissionsTabDetailsComponent);
